// ============================================================
// Anmeldestatus — der kleine Speicher, an dem die Wächter in App.tsx hängen.
// Kopie aus lilasprojekte (SPEC §2.1, §13.2).
//
// Drei Zustände, und der Startwert ist WICHTIG:
//   "unbekannt"  GET /me läuft noch → Wartezeichen, KEINE Umleitung
//   "anonym"     abgemeldet → Anmeldung, kein Toast
//   "angemeldet" alles offen
// Mit "anonym" als Startwert sähe ein angemeldeter Mensch bei jedem Neuladen für den
// Bruchteil einer Sekunde die Anmeldung.
//
// Die PERSON selbst (`Ich`) liegt NICHT hier, sondern in TanStack Query unter
// `['ich']` (`useIch()`): sie ist eine Serverantwort, und `PATCH /me` schreibt sie
// fort. Zwei Wahrheiten über dieselbe Person wären eine zu viel.
//
// Die 401-Registrierung steht am Fuß der Datei und läuft beim LADEN des Moduls:
// `api/client.ts` darf diesen Speicher nicht importieren (Kreis), also meldet der
// Speicher sich dort an.
// ============================================================
import { create } from "zustand";
import { setzeAbmeldeHandler } from "../api/client";
import { abmelden as apiAbmelden } from "../api/hooks/basis";

/** "gestoert": GET /me scheiterte OHNE 401 (500, Netz, 503 im Deploy) — die Sitzung kann
 *  gültig sein; App.tsx zeigt dann eine Störung und fragt weiter nach, statt still zur
 *  Anmeldung zu schicken (Prüfung Runde 2). */
export type AuthStatus = "unbekannt" | "anonym" | "angemeldet" | "gestoert";

interface AuthZustand {
  status: AuthStatus;
  /** GET /me war erfolgreich — oder POST /auth/verify. */
  angemeldet(): void;
  /** GET /me hat 401 geantwortet, oder irgendein anderer Aufruf. */
  abgemeldet(): void;
  /** GET /me scheiterte ohne 401 — Server gerade nicht erreichbar. */
  gestoert(): void;
  /** POST /auth/logout und Status zurück auf „anonym". */
  abmelden(): Promise<void>;
}

export const useAuthStore = create<AuthZustand>((set) => ({
  status: "unbekannt",

  angemeldet() {
    set({ status: "angemeldet" });
  },

  abgemeldet() {
    set({ status: "anonym" });
  },

  gestoert() {
    // Ein Riegel-401 (client.ts) hat schon „anonym" gesetzt — das bleibt.
    set((z) => (z.status === "anonym" ? z : { status: "gestoert" }));
  },

  async abmelden() {
    try {
      await apiAbmelden();
    } catch {
      // Auch ein fehlgeschlagener Abmeldeversuch beendet die Sitzung im Browser —
      // alles andere ließe jemanden vor einem Bildschirm sitzen, der so tut, als
      // wäre er noch angemeldet.
    }
    set({ status: "anonym" });
  },
}));

// EINMAL beim Laden des Moduls. main.tsx importiert App.tsx, App.tsx diesen Speicher
// — die Zeile läuft also, bevor die erste Anfrage hinausgeht.
setzeAbmeldeHandler(() => {
  useAuthStore.getState().abgemeldet();
});
